import {
  type ChartOrientation,
  getChartTypeDef,
  normalizeChartTypeId,
} from "./chartTypeRegistry";
import { mergePresentationOptions, setOptionPath } from "./chartOptionPaths";

import {
  fieldOptionToColumn,
  pathToHeader,
  type TableColumnMapping,
} from "./tableMappingBuilder";

export type ChartSliceMapping = Pick<TableColumnMapping, "header" | "field" | "path">;

export type WidgetChartMapping = {
  chartType: string;
  orientation?: ChartOrientation;
  optionTemplate: Record<string, unknown>;
  /** Explicit OpenSearch agg → value paths (pie slices, funnel steps, …). */
  slices?: ChartSliceMapping[];
};

function cartesianAxes(orientation: ChartOrientation) {
  if (orientation === "horizontal") {
    return {
      xAxis: { type: "value" },
      yAxis: { type: "category", data: [] as string[] },
    };
  }
  return {
    xAxis: { type: "category", data: [] as string[] },
    yAxis: { type: "value" },
  };
}

function cartesianSeries(chartType: string, orientation: ChartOrientation, title?: string) {
  const titleBlock = title ? { title: { text: title, left: "center" } } : {};
  const axes = cartesianAxes(orientation);
  return {
    ...titleBlock,
    tooltip: { trigger: "axis" },
    ...axes,
    series: [{ type: chartType, data: [] as number[] }],
  };
}

export function buildOptionTemplate(
  chartType: string,
  orientation: ChartOrientation = "vertical",
  title?: string
): Record<string, unknown> {
  const type = normalizeChartTypeId(chartType);
  const def = getChartTypeDef(type);
  const titleBlock = title ? { title: { text: title, left: "center" } } : {};

  switch (type) {
    case "pie":
      return {
        ...titleBlock,
        tooltip: { trigger: "item" },
        legend: { orient: "vertical", left: "left" },
        series: [{ type: "pie", radius: "55%", data: [] }],
      };
    case "doughnut":
      return {
        ...titleBlock,
        tooltip: { trigger: "item" },
        legend: { orient: "vertical", left: "left" },
        series: [{ type: "pie", radius: ["40%", "70%"], data: [] }],
      };
    case "rosePie":
      return {
        ...titleBlock,
        tooltip: { trigger: "item" },
        legend: { orient: "vertical", left: "left" },
        series: [{ type: "pie", radius: ["20%", "65%"], roseType: "area", data: [] }],
      };
    case "funnel":
      return {
        ...titleBlock,
        tooltip: { trigger: "item" },
        series: [{ type: "funnel", left: "10%", width: "80%", data: [] }],
      };
    case "radar":
      return {
        ...titleBlock,
        tooltip: {},
        radar: { indicator: [] },
        series: [{ type: "radar", data: [{ value: [], name: title || "" }] }],
      };
    case "gauge":
      return {
        ...titleBlock,
        series: [{ type: "gauge", min: 0, max: 100, detail: { formatter: "{value}" }, data: [{ value: 0 }] }],
      };
    case "parallel":
      return {
        ...titleBlock,
        parallelAxis: [] as unknown[],
        parallel: { left: "5%", right: "13%", bottom: "10%", top: "20%" },
        series: [{ type: "parallel", lineStyle: { width: 2 }, data: [] as unknown[] }],
      };
    case "sunburst":
      return {
        ...titleBlock,
        series: [{ type: "sunburst", radius: [0, "90%"], data: [] }],
      };
    case "treemap":
      return {
        ...titleBlock,
        series: [{ type: "treemap", data: [] }],
      };
    case "tree":
      return {
        ...titleBlock,
        series: [{ type: "tree", data: [], top: "5%", left: "15%", bottom: "5%", right: "20%", symbolSize: 8, label: { position: "left" }, leaves: { label: { position: "right" } }, emphasis: { focus: "descendant" }, expandAndCollapse: true, animationDuration: 550, animationDurationUpdate: 750 }],
      };
    case "heatmap":
      return {
        ...titleBlock,
        tooltip: { position: "top" },
        grid: { height: "60%", top: "15%" },
        xAxis: { type: "category", data: [] as string[], splitArea: { show: true } },
        yAxis: { type: "category", data: [] as string[], splitArea: { show: true } },
        visualMap: { min: 0, max: 10, calculable: true, orient: "horizontal", left: "center", bottom: "5%" },
        series: [{ type: "heatmap", data: [] as unknown[], label: { show: false } }],
      };
    case "calendarHeatmap":
      return {
        ...titleBlock,
        tooltip: { position: "top" },
        visualMap: { min: 0, max: 100, calculable: true, seriesIndex: [0], orient: "horizontal", left: "center", bottom: "5%" },
        calendar: { top: 80, left: 50, right: 30, cellSize: ["auto", 16], range: new Date().getFullYear(), itemStyle: { borderWidth: 0.5 } },
        series: [{ type: "heatmap", coordinateSystem: "calendar", data: [] as unknown[] }],
      };
    case "themeRiver":
      return {
        ...titleBlock,
        singleAxis: { top: 50, bottom: 50, axisTick: {}, axisLabel: {}, type: "time" },
        series: [{ type: "themeRiver", emphasis: { itemStyle: { shadowBlur: 20, shadowColor: "rgba(0, 0, 0, 0.8)" } }, data: [] as unknown[] }],
      };
    case "sankey":
      return {
        ...titleBlock,
        series: [{ type: "sankey", layout: "none", emphasis: { focus: "adjacency" }, data: [] as unknown[], links: [] as unknown[] }],
      };
    case "boxplot":
      return {
        ...titleBlock,
        tooltip: { trigger: "item" },
        ...cartesianAxes(orientation),
        series: [{ type: "boxplot", data: [] as unknown[] }],
      };
    case "candlestick":
      return {
        ...titleBlock,
        tooltip: { trigger: "axis" },
        ...cartesianAxes(orientation),
        series: [{ type: "candlestick", data: [] as unknown[] }],
      };
    case "stackedBar":
      return {
        ...cartesianSeries("bar", orientation, title),
        series: [{ type: "bar", stack: "total", data: [] as number[] }],
      };
    case "stackedLine":
      return {
        ...cartesianSeries("line", orientation, title),
        series: [{ type: "line", stack: "total", data: [] as number[] }],
      };
    case "stackedArea":
      return {
        ...cartesianSeries("line", orientation, title),
        series: [{ type: "line", stack: "total", areaStyle: {}, data: [] as number[] }],
      };
    case "mixed": {
      const base = cartesianSeries("bar", orientation, title);
      const axes = cartesianAxes(orientation);
      return {
        ...titleBlock,
        tooltip: { trigger: "axis" },
        ...axes,
        series: [
          { type: "bar", data: [] as number[] },
          { type: "line", data: [] as number[] },
        ],
      };
    }
    case "waterfall":
      return {
        ...cartesianSeries("bar", orientation, title),
        series: [{ type: "bar", stack: "waterfall", data: [] as number[] }],
      };
    case "pictorialBar":
      return {
        ...titleBlock,
        tooltip: { trigger: "axis" },
        ...cartesianAxes(orientation),
        series: [{ type: "pictorialBar", symbol: "roundRect", data: [] as number[] }],
      };
    case "scatter":
    case "effectScatter":
      return {
        ...titleBlock,
        ...cartesianAxes(orientation),
        series: [{ type: def?.seriesType || "scatter", data: [] }],
      };
    case "area":
      return {
        ...titleBlock,
        tooltip: { trigger: "axis" },
        ...cartesianAxes(orientation),
        series: [{ type: "line", areaStyle: {}, data: [] }],
      };
    case "line":
      return cartesianSeries("line", orientation, title);
    case "bar":
    default:
      return cartesianSeries("bar", orientation, title);
  }
}

export function buildWidgetChartMapping(
  chartType: string,
  orientation: ChartOrientation = "vertical",
  title?: string
): WidgetChartMapping {
  const type = normalizeChartTypeId(chartType);
  const def = getChartTypeDef(type);
  return {
    chartType: type,
    orientation: def?.supportsOrientation ? orientation : undefined,
    optionTemplate: buildOptionTemplate(type, orientation, title),
  };
}

export function parseWidgetChartMapping(raw: unknown): WidgetChartMapping {
  if (!raw || typeof raw !== "object") {
    return buildWidgetChartMapping("bar");
  }
  const m = raw as Record<string, unknown>;
  const chartType = normalizeChartTypeId(String(m.chartType || "bar"));
  const orientation: ChartOrientation =
    m.orientation === "horizontal" ? "horizontal" : "vertical";
  const optionTemplate =
    m.optionTemplate && typeof m.optionTemplate === "object"
      ? (m.optionTemplate as Record<string, unknown>)
      : buildOptionTemplate(chartType, orientation);
  return { chartType, orientation, optionTemplate, slices: parseChartSlices(m.slices) };
}

export function parseChartSlices(raw: unknown): ChartSliceMapping[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const slices: ChartSliceMapping[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const s = item as Record<string, unknown>;
    const field = String(s.field ?? "").trim();
    const path =
      s.path != null
        ? String(s.path).trim()
        : s.source != null
          ? String(s.source).trim()
          : "doc_count";
    const header = String(s.header || s.label || "").trim();
    if (!field) continue;
    slices.push({
      field,
      path: path || "doc_count",
      header: header || pathToHeader(field),
    });
  }
  return slices;
}

export function applyChartTypeChange(
  current: WidgetChartMapping,
  nextChartType: string,
  title?: string
): WidgetChartMapping {
  const type = normalizeChartTypeId(nextChartType);
  const def = getChartTypeDef(type);
  const orientation = def?.supportsOrientation ? current.orientation || "vertical" : undefined;
  const base = buildWidgetChartMapping(type, orientation || "vertical", title);
  return {
    ...base,
    slices: current.slices,
    optionTemplate: mergePresentationOptions(current.optionTemplate, base.optionTemplate),
  };
}

export function applyOrientationChange(
  current: WidgetChartMapping,
  orientation: ChartOrientation,
  title?: string
): WidgetChartMapping {
  const def = getChartTypeDef(current.chartType);
  if (!def?.supportsOrientation) return current;
  const base = buildWidgetChartMapping(current.chartType, orientation, title);
  return {
    chartType: current.chartType,
    orientation,
    slices: current.slices,
    optionTemplate: mergePresentationOptions(current.optionTemplate, base.optionTemplate),
  };
}

export function applyOptionFieldChange(
  mapping: WidgetChartMapping,
  path: string,
  value: unknown
): WidgetChartMapping {
  return {
    ...mapping,
    optionTemplate: setOptionPath(mapping.optionTemplate, path, value),
  };
}

export function applyOptionFieldsChange(
  mapping: WidgetChartMapping,
  updates: Array<{ path: string; value: unknown }>
): WidgetChartMapping {
  return updates.reduce(
    (next, { path, value }) => applyOptionFieldChange(next, path, value),
    mapping
  );
}

export function mappingToJson(mapping: WidgetChartMapping): string {
  return JSON.stringify(
    {
      chartType: mapping.chartType,
      ...(mapping.orientation ? { orientation: mapping.orientation } : {}),
      ...(mapping.slices && mapping.slices.length > 0 ? { slices: mapping.slices } : {}),
      optionTemplate: mapping.optionTemplate,
    },
    null,
    2
  );
}
